import { useState } from 'react';
import { Activity, CalendarClock, CheckCircle2, HelpCircle, Clock3, Database, HardDrive, ShieldAlert, XCircle } from 'lucide-react';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@easyflow/ui-master/components/ui/card';
import type { RestoreVerificationRun, RestoreVerificationSummary } from '@easyflow/shared';

interface RestoreVerificationsProps {
  summary: RestoreVerificationSummary | undefined;
  bridgeHealthy: boolean;
}

const STATUS_LABEL: Record<RestoreVerificationRun['status'], string> = {
  pass: 'Superato',
  fail: 'Fallito',
  skip: 'Saltato',
  running: 'In corso',
};

function dateLabel(value: string | null | undefined): string {
  if (!value) return 'Sconosciuto';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Sconosciuto' : date.toLocaleString('it-IT', { timeZone: 'Europe/Rome' });
}

function durationLabel(value: number | null): string {
  if (value === null) return 'Sconosciuta';
  if (value < 60) return `${Math.round(value)} s`;
  const minutes = Math.floor(value / 60);
  const seconds = Math.round(value % 60);
  return seconds ? `${minutes} min ${seconds} s` : `${minutes} min`;
}

function StatusBadge({ status }: { status: RestoreVerificationRun['status'] | null }) {
  if (!status) {
    return <Badge variant="outline" className="gap-1 border-slate-300 text-slate-600"><HelpCircle className="h-3 w-3" /> Sconosciuto</Badge>;
  }
  const style = status === 'pass'
    ? 'border-emerald-300 bg-emerald-500/10 text-emerald-800'
    : status === 'fail'
      ? 'border-rose-300 bg-rose-500/10 text-rose-800'
      : status === 'running'
        ? 'border-sky-300 bg-sky-500/10 text-sky-800'
        : 'border-amber-300 bg-amber-500/10 text-amber-800';
  const Icon = status === 'pass' ? CheckCircle2 : status === 'fail' ? XCircle : status === 'running' ? Activity : HelpCircle;
  return <Badge variant="outline" className={`gap-1 ${style}`}><Icon className="h-3 w-3" /> {STATUS_LABEL[status]}</Badge>;
}

function LatestRun({ kind, run, bridgeHealthy }: { kind: 'physical' | 'logical'; run: RestoreVerificationRun | null; bridgeHealthy: boolean }) {
  const physical = kind === 'physical';
  const Icon = physical ? HardDrive : Database;
  const finishedMs = run?.finished_at ? Date.parse(run.finished_at) : NaN;
  const expired = run?.status === 'pass' && (!Number.isFinite(finishedMs) || Date.now() - finishedMs > 30 * 86400_000 || finishedMs > Date.now());
  return (
    <div className="rounded-lg border bg-background/70 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-medium">
          <span className={`grid h-8 w-8 place-items-center rounded-md ${physical ? 'bg-indigo-500/10 text-indigo-700' : 'bg-teal-500/10 text-teal-700'}`}>
            <Icon className="h-4 w-4" />
          </span>
          Restore {physical ? 'fisico' : 'logico'}
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={run?.status ?? null} />
          {expired && <Badge variant="outline" className="border-amber-300 text-amber-800">Scaduto</Badge>}
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 text-xs sm:grid-cols-4">
        <div><div className="text-muted-foreground">Ultima verifica</div><div className="mt-1 font-medium">{dateLabel(run?.finished_at ?? run?.started_at)}</div></div>
        <div><div className="text-muted-foreground">Durata</div><div className="mt-1 font-medium">{run ? durationLabel(run.duration_seconds) : 'Sconosciuta'}</div></div>
        <div><div className="text-muted-foreground">Nodo</div><div className="mt-1 truncate font-mono" title={run?.node ?? undefined}>{run?.node ?? 'Sconosciuto'}</div></div>
        <div><div className="text-muted-foreground">Target</div><div className="mt-1 truncate font-mono" title={run?.target ?? undefined}>{run?.target ?? 'Sconosciuto'}</div></div>
      </div>
      {!bridgeHealthy && <p className="mt-3 border-t pt-3 text-xs text-amber-800">Snapshot non aggiornato: stato e pianificazione non sono una conferma corrente.</p>}
    </div>
  );
}

export function RestoreVerifications({ summary, bridgeHealthy }: RestoreVerificationsProps) {
  const [selectedRun, setSelectedRun] = useState<string | null>(null);
  const retention = bridgeHealthy ? summary?.retention ?? 'unknown' : 'unknown';
  const retentionLabel = retention === 'eligible' ? 'Idoneo' : retention === 'blocked' ? 'Bloccato' : 'Sconosciuto';
  const retentionStyle = retention === 'eligible'
    ? 'border-emerald-300 bg-emerald-500/10 text-emerald-800'
    : retention === 'blocked'
      ? 'border-rose-300 bg-rose-500/10 text-rose-800'
      : 'border-slate-300 bg-slate-500/5 text-slate-700';
  const runs = summary?.history ?? [];
  const physicalSchedule = summary?.physical_schedule;
  const logicalSchedule = summary?.logical_schedule;

  return (
    <Card id="restore-verifications" className="scroll-mt-24 border-indigo-200/80">
      <CardHeader className="border-b bg-indigo-500/[0.035] pb-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <ShieldAlert className="h-4 w-4 text-indigo-700" />
              Verifiche di ripristino
            </CardTitle>
            <CardDescription className="mt-1 max-w-3xl">
              Le verifiche attestano un ripristino realmente eseguito. Un backup riuscito, da solo, non dimostra che i dati siano recuperabili.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-muted-foreground">Retention</span>
            <Badge variant="outline" className={retentionStyle}>{retentionLabel}</Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5 pt-5">
        <p className="text-xs text-muted-foreground">
          Orari Europe/Rome. {retention === 'eligible'
            ? 'Entrambi i gate sono recenti: gli scheduler esistenti possono eliminare i backup scaduti secondo le regole attuali.'
            : retention === 'blocked'
              ? 'Eliminazione dei backup bloccata: servono entrambi i restore verificati entro 30 giorni.'
              : 'Stato corrente non certificabile: nessuna autorizzazione alla pulizia può essere dedotta da questa schermata.'}
        </p>
        {!summary && (
          <div className="flex gap-3 rounded-md border border-dashed bg-muted/30 p-4 text-sm">
            <HelpCircle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div><p className="font-medium">Evidenza di restore non disponibile</p><p className="mt-1 text-xs text-muted-foreground">Il collector non ha fornito verifiche. Stato, prossime esecuzioni e retention restano sconosciuti.</p></div>
          </div>
        )}
        <div className="grid gap-3 lg:grid-cols-2">
          <LatestRun kind="physical" run={summary?.physical ?? null} bridgeHealthy={bridgeHealthy} />
          <LatestRun kind="logical" run={summary?.logical ?? null} bridgeHealthy={bridgeHealthy} />
        </div>

        <section aria-label="Pianificazione verifiche" className="rounded-lg border">
          <div className="flex items-center gap-2 border-b px-4 py-3 text-sm font-medium"><CalendarClock className="h-4 w-4 text-muted-foreground" /> Prossime esecuzioni</div>
          <div className="grid divide-y sm:grid-cols-2 sm:divide-x sm:divide-y-0">
            {([
              ['Fisica', physicalSchedule],
              ['Logica', logicalSchedule],
            ] as const).map(([label, schedule]) => {
              const observedMs = schedule?.observed_at ? Date.parse(schedule.observed_at) : NaN;
              const nextMs = schedule?.next_at ? Date.parse(schedule.next_at) : NaN;
              const canPromise = bridgeHealthy && schedule?.active && schedule.next_at
                && Number.isFinite(observedMs) && Date.now() >= observedMs
                && Date.now() - observedMs <= 45 * 60_000 && nextMs > Date.now();
              return (
                <div key={label} className="p-4">
                  <div className="text-xs text-muted-foreground">Verifica {label.toLowerCase()}</div>
                  <div className="mt-1 flex items-center gap-2 text-sm font-medium">
                    <Clock3 className="h-4 w-4 text-indigo-600" />
                    {canPromise ? dateLabel(schedule.next_at) : 'Nessuna esecuzione confermata'}
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    {!bridgeHealthy
                      ? 'Ponte dati stantio: non è possibile promettere un orario.'
                      : !schedule
                        ? 'Pianificazione sconosciuta.'
                        : !schedule.active
                          ? schedule.reason ?? 'Pianificazione non attiva.'
                          : schedule.next_at
                            ? `Calendario ${schedule.calendar ?? 'non specificato'} · rilevata ${dateLabel(schedule.observed_at)}`
                            : schedule.reason ?? 'Prossimo orario non disponibile.'}
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        <section>
          <div className="mb-2 flex items-baseline justify-between gap-2">
            <h3 className="text-sm font-medium">Storico verifiche</h3>
            <span className="text-xs text-muted-foreground">{runs.length} {runs.length === 1 ? 'esecuzione' : 'esecuzioni'}</span>
          </div>
          {runs.length === 0 ? (
            <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
              {summary ? 'Nessuna verifica nello storico disponibile.' : 'Lo storico non è disponibile senza dati del collector.'}
            </div>
          ) : (
            <div className="divide-y rounded-md border">
              {runs.map((run) => {
                const isOpen = selectedRun === run.run_id;
                return (
                  <div key={run.run_id}>
                    <button
                      type="button"
                      className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-muted/40 sm:grid-cols-[1.1fr_0.8fr_0.8fr_auto]"
                      aria-expanded={isOpen}
                      onClick={() => setSelectedRun(isOpen ? null : run.run_id)}
                    >
                      <span className="min-w-0"><span className="block truncate text-sm font-medium">{run.kind === 'physical' ? 'Fisica' : 'Logica'}</span><span className="block truncate font-mono text-[11px] text-muted-foreground">{run.run_id}</span></span>
                      <span className="hidden text-xs text-muted-foreground sm:block">{dateLabel(run.finished_at ?? run.started_at)}</span>
                      <span className="hidden text-xs text-muted-foreground sm:block">{durationLabel(run.duration_seconds)}</span>
                      <StatusBadge status={run.status} />
                    </button>
                    {isOpen && (
                      <div className="grid gap-3 border-t bg-muted/20 px-4 py-3 text-xs sm:grid-cols-4">
                        <div><span className="text-muted-foreground">Nodo</span><p className="mt-1 font-mono">{run.node ?? 'Sconosciuto'}</p></div>
                        <div><span className="text-muted-foreground">Target</span><p className="mt-1 font-mono">{run.target ?? 'Sconosciuto'}</p></div>
                        <div><span className="text-muted-foreground">Inizio / fine</span><p className="mt-1">{dateLabel(run.started_at)}<br />{dateLabel(run.finished_at)}</p></div>
                        <div className={run.last_error ? 'sm:col-span-4' : ''}><span className="text-muted-foreground">Dettaglio esito</span><p className={`mt-1 ${run.last_error ? 'text-rose-800' : ''}`}>{run.last_error ?? (run.status === 'pass' ? 'Nessun errore riportato.' : 'Motivazione non riportata dal collector.')}</p></div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </CardContent>
    </Card>
  );
}
